"use client";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Icons } from "@assets/assets";
import Image, { StaticImageData } from "next/image";
import IndustryBenefitCard from "./IndsutryBenifitCard";

export type BenefitItem = {
  icon: StaticImageData | string | undefined;
  title: string[] | string;
  description: string;
};

export type IndustryBenifitsProps = {
  benefits: BenefitItem[];
};

const getCardsPerView = (width: number): number => {
  if (width >= 1024) return 4;
  if (width >= 768) return 3;
  if (width >= 640) return 2;
  return 1;
};

// Helper to get responsive padding
const getResponsivePadding = (width: number): number => {
  return width < 640 ? 20 : 48;
};

export default function IndustryBenifitsScroll({ benefits }: IndustryBenifitsProps) {
  const [currentIndex, setCurrentIndex] = useState(benefits.length);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [cardWidth, setCardWidth] = useState(0);
  const [padding, setPadding] = useState(48); // Dynamic padding state

  const trackRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isAnimating = useRef(false);

  const looped = [...benefits, ...benefits, ...benefits];

  useEffect(() => {
    const measure = () => {
      const containerWidth = containerRef.current?.offsetWidth ?? window.innerWidth;
      const currentPadding = getResponsivePadding(containerWidth);

      const usableWidth = containerWidth - currentPadding * 2;
      const perView = getCardsPerView(containerWidth);

      setPadding(currentPadding);
      setCardWidth(usableWidth / perView);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const goNext = useCallback(() => {
    if (isAnimating.current) return;
    setCurrentIndex((prev) => prev + 1);
  }, []);

  const goPrev = useCallback(() => {
    if (isAnimating.current) return;
    setCurrentIndex((prev) => prev - 1);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || cardWidth === 0) return;

    const handleTransitionEnd = () => {
      let needsJump = false;
      let targetIndex = currentIndex;

      if (currentIndex <= benefits.length - 1) {
        targetIndex = currentIndex + benefits.length;
        needsJump = true;
      } else if (currentIndex >= benefits.length * 2) {
        targetIndex = currentIndex - benefits.length;
        needsJump = true;
      }

      if (needsJump) {
        isAnimating.current = true;
        track.style.transition = "none";
        setCurrentIndex(targetIndex);

        requestAnimationFrame(() => {
          track.offsetHeight;
          requestAnimationFrame(() => {
            track.style.transition = "transform 500ms cubic-bezier(0.22, 1, 0.36, 1)";
            isAnimating.current = false;
          });
        });
      } else {
        isAnimating.current = false;
      }
    };

    track.addEventListener("transitionend", handleTransitionEnd);
    return () => track.removeEventListener("transitionend", handleTransitionEnd);
  }, [currentIndex, benefits.length, cardWidth]);

  const progress =
    (((currentIndex % benefits.length) + benefits.length) % benefits.length + 1) /
    benefits.length;

  const handleToggleExpand = useCallback((e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setExpandedIndex((prev) => (prev === index ? null : index));
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      {/* Navigation */}
      <div
        className="mt-6 sm:mt-8 flex justify-end"
        style={{ paddingRight: padding }}
      >
        <div className="flex gap-2 sm:gap-[10px]">
          <button onClick={goPrev} className="cursor-pointer hover:opacity-70 transition">
            <Image src={Icons.slideLeft} alt="" width={40} height={40} className="w-[32px] h-[32px] sm:w-[40px] sm:h-[40px]" />
          </button>
          <button onClick={goNext} className="cursor-pointer hover:opacity-70 transition">
            <Image src={Icons.slideRight} alt="" width={40} height={40} className="w-[32px] h-[32px] sm:w-[40px] sm:h-[40px]" />
          </button>
        </div>
      </div>

      {/* Slider */}
      <div
        className="w-full mt-6"
        style={{ paddingLeft: padding, paddingRight: padding }}
      >
        <div className="overflow-hidden w-full">
          <div
            ref={trackRef}
            className="flex items-start"
            style={{
              transform: `translate3d(-${currentIndex * cardWidth}px, 0, 0)`,
              transition: "transform 500ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
            onTransitionStart={() => { isAnimating.current = true; }}
          >
            {looped.map((item, index) => (
              <div key={index} style={{ width: `${cardWidth}px`, minWidth: `${cardWidth}px`, flexShrink: 0 }}>
                <IndustryBenefitCard
                  item={item}
                  index={index % benefits.length}
                  isExpanded={expandedIndex === index}
                  onToggle={(e) => handleToggleExpand(e, index)}
                  onClick={() => { if (!isAnimating.current) setCurrentIndex(index); }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div
        className="pb-[30px] lg:pb-[100px]"
        style={{ paddingLeft: padding, paddingRight: padding }}
      >
        <div className="mt-6 sm:mt-8 h-2 sm:h-3 bg-white/5 w-full sm:w-[40%] md:w-[25%] lg:w-[20%] relative overflow-hidden rounded-full">
          <div
            className="h-full bg-[#1D4ED8] transition-transform duration-300 ease-out origin-left"
            style={{ transform: `scaleX(${progress})`, width: "100%" }}
          />
        </div>
      </div>
    </div>
  );
}